import {
  MONITOR_STATUS_LABELS,
  subject,
  type AssetMonitoring,
  type AssetStatusEvent,
  type MonitoringConfigInput,
  type MonitoringEvent,
} from '@ftthnms/shared';
import { Button, StatusIndicator } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, ExternalLink, FlaskConical, Save } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ApiError, dateTime, nf, nf1, send } from '../lib/api';
import { ago, toIndicator, uptime, useAssetMonitoring, webUiUrl } from '../lib/monitoring';
import { useSession } from '../lib/session';
import { useToast } from './Toast';

/** Monitoring tab of an asset (MON-04..08, MON-13). */
export function MonitoringPanel({ assetId, areaCode }: { assetId: string; areaCode: string | null }) {
  const { ability } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const mon = useAssetMonitoring(assetId);
  const [pinging, setPinging] = useState(false);
  const target = subject('Monitoring', { areaCode });

  if (mon.isPending) return <span className="fn-spinner" aria-label="Memuat monitoring" />;
  if (mon.error)
    return (
      <div className="fn-alert fn-alert--danger">
        <div className="fn-alert__body">{mon.error.message}</div>
      </div>
    );
  const m = mon.data;

  if (!m.ip) {
    return (
      <p className="fn-hint">
        Aset ini belum punya alamat IP. Isi IP di data aset (Edit) supaya bisa dipantau.
      </p>
    );
  }

  async function ping() {
    setPinging(true);
    try {
      const r = await send<AssetStatusEvent>('POST', `/api/v1/monitoring/assets/${assetId}/ping`);
      await qc.invalidateQueries({ queryKey: ['asset', assetId, 'monitoring'] });
      toast({
        tone: r.status === 'online' ? 'success' : r.status === 'offline' ? 'danger' : 'warning',
        text:
          r.latencyMs !== null
            ? `${MONITOR_STATUS_LABELS[r.status]} · ${nf1.format(r.latencyMs)} ms · loss ${r.lossPct}%`
            : `Tidak ada balasan dari ${m.ip}`,
      });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setPinging(false);
    }
  }

  const d = m.device;
  const web = d ? webUiUrl(d.ip, d.httpsPort, d.httpPort) : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="fn-card" style={{ background: 'var(--bg-surface-inset)' }}>
        <div className="fn-card__header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <StatusIndicator
              status={toIndicator(d?.status)}
              meta={d?.since ? `sejak ${ago(d.since)}` : undefined}
            />
            <span className="mono" style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {m.ip}
              {!m.config.enabled && ' · pemantauan mati'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {web && (
              <a
                className="fn-icon-btn fn-icon-btn--sm"
                href={web}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Buka web UI perangkat"
                title={`Buka ${web}`}
              >
                <ExternalLink className="i i--sm" aria-hidden />
              </a>
            )}
            {ability.can('ping', target) && (
              <Button
                size="sm"
                variant="primary"
                icon={<Activity className="i" aria-hidden />}
                loading={pinging}
                onClick={() => void ping()}
              >
                Ping
              </Button>
            )}
          </div>
        </div>
        <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <Mini label="Latensi" value={d?.latencyMs == null ? '—' : `${nf1.format(d.latencyMs)} ms`} />
            <Mini label="Packet loss" value={d?.lossPct == null ? '—' : `${nf.format(d.lossPct)}%`} />
            <Mini
              label="Uptime 24 jam"
              value={m.uptimePct24h === null ? '—' : `${nf1.format(m.uptimePct24h)}%`}
            />
          </div>
          <LatencyChart checks={m.checks} warnMs={m.config.latencyWarnMs} />
          <span className="fn-hint">
            Dicek {ago(d?.lastCheckedAt)} · tiap{' '}
            {m.config.intervalSec >= 60
              ? `${m.config.intervalSec / 60} menit`
              : `${m.config.intervalSec} detik`}
            {d?.lastError && d.status !== 'online' ? ` · ${d.lastError}` : ''}
          </span>
        </div>
      </section>

      {m.snmp && (
        <section className="fn-drawer__section">
          <span className="fn-label">SNMP · {ago(m.snmp.collectedAt)}</span>
          <dl className="fn-dl">
            {m.snmp.sysName && (
              <>
                <dt>Nama</dt>
                <dd className="mono">{m.snmp.sysName}</dd>
              </>
            )}
            {m.snmp.sysDescr && (
              <>
                <dt>Perangkat</dt>
                <dd>{m.snmp.sysDescr}</dd>
              </>
            )}
            <dt>Uptime</dt>
            <dd>{uptime(m.snmp.uptimeS)}</dd>
            {m.snmp.cpuPct !== null && (
              <>
                <dt>CPU</dt>
                <dd className="num">{nf1.format(m.snmp.cpuPct)}%</dd>
              </>
            )}
            {m.snmp.memPct !== null && (
              <>
                <dt>Memori</dt>
                <dd className="num">
                  {nf1.format(m.snmp.memPct)}%
                  {m.snmp.memTotalMb ? ` dari ${nf.format(Math.round(m.snmp.memTotalMb))} MB` : ''}
                </dd>
              </>
            )}
            {m.snmp.sysLocation && (
              <>
                <dt>Lokasi</dt>
                <dd>{m.snmp.sysLocation}</dd>
              </>
            )}
          </dl>
        </section>
      )}

      {m.events.length > 0 && <EventFeed events={m.events.slice(0, 8)} />}

      {ability.can('configure', target) && <ConfigForm assetId={assetId} m={m} />}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="fn-kpi__label">{label}</div>
      <div className="num" style={{ font: '700 15px/22px var(--font-sans)' }}>
        {value}
      </div>
    </div>
  );
}

/** 24 h latency line; failed checks are red ticks on the baseline. */
function LatencyChart({ checks, warnMs }: { checks: AssetMonitoring['checks']; warnMs: number }) {
  if (checks.length < 2) return <p className="fn-hint">Grafik muncul setelah beberapa kali pengecekan.</p>;
  const W = 360;
  const H = 64;
  const t0 = Date.parse(checks[0]!.at);
  const t1 = Math.max(Date.parse(checks[checks.length - 1]!.at), t0 + 1);
  const max = Math.max(warnMs * 1.2, ...checks.map((c) => c.latencyMs ?? 0));
  const x = (at: string) => ((Date.parse(at) - t0) / (t1 - t0)) * W;
  const y = (ms: number) => H - 4 - (ms / max) * (H - 10);
  const pts = checks
    .filter((c) => c.latencyMs !== null)
    .map((c) => `${x(c.at).toFixed(1)},${y(c.latencyMs!).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      role="img"
      aria-label="Grafik latensi 24 jam"
      style={{ display: 'block' }}
    >
      <line
        x1={0}
        x2={W}
        y1={y(warnMs)}
        y2={y(warnMs)}
        stroke="var(--warning)"
        strokeDasharray="3 4"
        strokeWidth={1}
        opacity={0.6}
      />
      {pts && (
        <polyline points={pts} fill="none" stroke="var(--primary)" strokeWidth={1.8} strokeLinejoin="round" />
      )}
      {checks
        .filter((c) => c.latencyMs === null)
        .map((c) => (
          <line
            key={c.at}
            x1={x(c.at)}
            x2={x(c.at)}
            y1={H - 12}
            y2={H}
            stroke="var(--danger)"
            strokeWidth={2}
          />
        ))}
    </svg>
  );
}

export function EventFeed({
  events,
  showAsset,
  onOpen,
}: {
  events: MonitoringEvent[];
  showAsset?: boolean;
  onOpen?: (id: string) => void;
}) {
  return (
    <section className="fn-drawer__section">
      <span className="fn-label">Perubahan status</span>
      <div className="fn-feed">
        {events.map((e) => (
          <div key={e.id} className="fn-feed__item" style={{ paddingInline: 0 }}>
            <span className={`fn-status fn-status--${toIndicator(e.to)}`} style={{ paddingTop: 5 }}>
              <span className="fn-status__dot" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="fn-feed__title">
                {showAsset && (
                  <button
                    type="button"
                    className="fn-link"
                    onClick={() => onOpen?.(e.assetId)}
                    style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                  >
                    {e.code}
                  </button>
                )}
                {showAsset ? ' → ' : ''}
                {MONITOR_STATUS_LABELS[e.to]}
              </div>
              <div className="fn-feed__meta">
                dari {MONITOR_STATUS_LABELS[e.from].toLowerCase()}
                {e.message ? ` · ${e.message}` : ''}
              </div>
            </div>
            <div className="fn-feed__time" title={dateTime.format(new Date(e.at))}>
              {ago(e.at)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const INTERVALS: Array<[number, string]> = [
  [60, '1 menit'],
  [300, '5 menit'],
  [900, '15 menit'],
  [1800, '30 menit'],
  [3600, '1 jam'],
];

/** Settings (Admin/NOC). SNMP secrets are write-only: the field stays empty and keeps the stored value. */
function ConfigForm({ assetId, m }: { assetId: string; m: AssetMonitoring }) {
  const qc = useQueryClient();
  const toast = useToast();
  const c = m.config;
  const [v, setV] = useState({
    enabled: c.enabled,
    intervalSec: c.intervalSec,
    failThreshold: String(c.failThreshold),
    latencyWarnMs: String(c.latencyWarnMs),
    lossWarnPct: String(c.lossWarnPct),
    maintenanceUntil: c.maintenanceUntil ? toLocalInput(c.maintenanceUntil) : '',
    snmpEnabled: c.snmp?.enabled ?? false,
    version: (c.snmp?.version ?? '2c') as '1' | '2c' | '3',
    port: String(c.snmp?.port ?? 161),
    community: '',
    user: c.snmp?.user ?? '',
    authProtocol: (c.snmp?.authProtocol ?? 'sha') as 'md5' | 'sha',
    authKey: '',
    privProtocol: (c.snmp?.privProtocol ?? 'aes') as 'des' | 'aes',
    privKey: '',
  });
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
  const set = <K extends keyof typeof v>(k: K, value: (typeof v)[K]) => setV((s) => ({ ...s, [k]: value }));

  const snmp = () =>
    v.version === '3'
      ? {
          enabled: v.snmpEnabled,
          version: '3' as const,
          port: Number(v.port),
          user: v.user,
          authProtocol: v.authProtocol,
          authKey: v.authKey || undefined,
          privProtocol: v.privProtocol,
          privKey: v.privKey || undefined,
        }
      : {
          enabled: v.snmpEnabled,
          version: v.version,
          port: Number(v.port),
          community: v.community || undefined,
        };

  async function save() {
    setBusy('save');
    setError(null);
    try {
      const body: MonitoringConfigInput = {
        enabled: v.enabled,
        intervalSec: v.intervalSec,
        failThreshold: Number(v.failThreshold),
        latencyWarnMs: Number(v.latencyWarnMs),
        lossWarnPct: Number(v.lossWarnPct),
        maintenanceUntil: v.maintenanceUntil ? new Date(v.maintenanceUntil).toISOString() : null,
        snmp: v.snmpEnabled || c.snmp ? snmp() : null,
      };
      await send('PUT', `/api/v1/monitoring/assets/${assetId}/config`, body);
      await qc.invalidateQueries({ queryKey: ['asset', assetId, 'monitoring'] });
      await qc.invalidateQueries({ queryKey: ['monitoring'] });
      toast({ tone: 'success', text: 'Pengaturan monitoring tersimpan.' });
      setV((s) => ({ ...s, community: '', authKey: '', privKey: '' }));
    } catch (err) {
      setError(
        err instanceof ApiError && Object.values(err.fieldErrors)[0]
          ? `${err.message} ${Object.values(err.fieldErrors)[0]}`
          : (err as Error).message,
      );
    } finally {
      setBusy(null);
    }
  }

  async function runTest() {
    setBusy('test');
    setTest(null);
    try {
      const r = await send<{
        ok: boolean;
        error?: string;
        result?: { sysName: string | null; sysDescr: string | null; uptimeS: number | null };
      }>('POST', `/api/v1/monitoring/assets/${assetId}/snmp/test`, snmp());
      setTest(
        r.ok
          ? {
              ok: true,
              text: `Terhubung: ${r.result?.sysName ?? '(tanpa nama)'} · ${r.result?.sysDescr?.slice(0, 80) ?? ''} · uptime ${uptime(r.result?.uptimeS)}`,
            }
          : { ok: false, text: r.error ?? 'Gagal' },
      );
    } catch (err) {
      setTest({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  const stored = c.snmp?.hasCredentials;
  return (
    <section className="fn-card">
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Pengaturan monitoring</h3>
          <p className="fn-card__desc">
            Offline setelah beberapa kali gagal berturut-turut, supaya tidak berkedip.
          </p>
        </div>
      </div>
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Switch label="Pantau perangkat ini" checked={v.enabled} onChange={(x) => set('enabled', x)} />
        <div className="fn-form-grid">
          <Field label="Interval ping">
            <select
              className="fn-input"
              value={v.intervalSec}
              onChange={(e) => set('intervalSec', Number(e.target.value))}
            >
              {INTERVALS.map(([s, l]) => (
                <option key={s} value={s}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Offline setelah gagal">
            <select
              className="fn-input"
              value={v.failThreshold}
              onChange={(e) => set('failThreshold', e.target.value)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}× berturut-turut
                </option>
              ))}
            </select>
          </Field>
          <Field label="Warning bila latensi >">
            <Suffix unit="ms">
              <input
                className="fn-input fn-input--mono"
                inputMode="numeric"
                value={v.latencyWarnMs}
                onChange={(e) => set('latencyWarnMs', e.target.value)}
              />
            </Suffix>
          </Field>
          <Field label="Warning bila loss ≥">
            <Suffix unit="%">
              <input
                className="fn-input fn-input--mono"
                inputMode="numeric"
                value={v.lossWarnPct}
                onChange={(e) => set('lossWarnPct', e.target.value)}
              />
            </Suffix>
          </Field>
          <Field
            label="Maintenance sampai"
            wide
            hint="Status Maintenance, tidak dianggap gangguan. Kosongkan untuk selesai."
          >
            <input
              className="fn-input"
              type="datetime-local"
              value={v.maintenanceUntil}
              onChange={(e) => set('maintenanceUntil', e.target.value)}
            />
          </Field>
        </div>

        <Switch
          label="SNMP (nama, uptime, CPU, memori)"
          checked={v.snmpEnabled}
          onChange={(x) => set('snmpEnabled', x)}
        />
        {(v.snmpEnabled || c.snmp) && (
          <div className="fn-form-grid">
            <Field label="Versi">
              <select
                className="fn-input"
                value={v.version}
                onChange={(e) => set('version', e.target.value as '1' | '2c' | '3')}
              >
                <option value="2c">v2c</option>
                <option value="1">v1</option>
                <option value="3">v3</option>
              </select>
            </Field>
            <Field label="Port">
              <input
                className="fn-input fn-input--mono"
                inputMode="numeric"
                value={v.port}
                onChange={(e) => set('port', e.target.value)}
              />
            </Field>
            {v.version !== '3' ? (
              <Field
                label="Community"
                wide
                hint={stored ? 'Tersimpan (terenkripsi). Isi hanya untuk mengganti.' : undefined}
              >
                <input
                  className="fn-input fn-input--mono"
                  type="password"
                  autoComplete="new-password"
                  value={v.community}
                  placeholder={stored ? '••••••••' : 'public'}
                  onChange={(e) => set('community', e.target.value)}
                />
              </Field>
            ) : (
              <>
                <Field label="User" wide>
                  <input
                    className="fn-input fn-input--mono"
                    autoComplete="off"
                    value={v.user}
                    onChange={(e) => set('user', e.target.value)}
                  />
                </Field>
                <Field label="Auth">
                  <select
                    className="fn-input"
                    value={v.authProtocol}
                    onChange={(e) => set('authProtocol', e.target.value as 'md5' | 'sha')}
                  >
                    <option value="sha">SHA</option>
                    <option value="md5">MD5</option>
                  </select>
                </Field>
                <Field label="Auth key" hint={stored ? 'Kosong = tetap' : undefined}>
                  <input
                    className="fn-input fn-input--mono"
                    type="password"
                    autoComplete="new-password"
                    value={v.authKey}
                    onChange={(e) => set('authKey', e.target.value)}
                  />
                </Field>
                <Field label="Privacy">
                  <select
                    className="fn-input"
                    value={v.privProtocol}
                    onChange={(e) => set('privProtocol', e.target.value as 'des' | 'aes')}
                  >
                    <option value="aes">AES</option>
                    <option value="des">DES</option>
                  </select>
                </Field>
                <Field label="Privacy key" hint={stored ? 'Kosong = tetap' : undefined}>
                  <input
                    className="fn-input fn-input--mono"
                    type="password"
                    autoComplete="new-password"
                    value={v.privKey}
                    onChange={(e) => set('privKey', e.target.value)}
                  />
                </Field>
              </>
            )}
          </div>
        )}
        {test && (
          <div className={`fn-alert fn-alert--${test.ok ? 'success' : 'danger'}`} role="status">
            <div className="fn-alert__body">{test.text}</div>
          </div>
        )}
        {error && <span className="fn-hint fn-hint--error">{error}</span>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {(v.snmpEnabled || c.snmp) && (
            <Button
              size="sm"
              icon={<FlaskConical className="i" aria-hidden />}
              loading={busy === 'test'}
              onClick={() => void runTest()}
            >
              Tes SNMP
            </Button>
          )}
          <Button
            size="sm"
            variant="primary"
            icon={<Save className="i" aria-hidden />}
            loading={busy === 'save'}
            onClick={() => void save()}
          >
            Simpan
          </Button>
        </div>
      </div>
    </section>
  );
}

function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="fn-choice" style={{ cursor: 'pointer' }}>
      <span className="fn-choice__title">{label}</span>
      <span className="fn-switch">
        <input
          className="fn-switch__input"
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="fn-switch__track" />
      </span>
    </label>
  );
}

function Field({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`fn-field${wide ? ' span-2' : ''}`}>
      <span className="fn-label">{label}</span>
      {children}
      {hint && <span className="fn-hint">{hint}</span>}
    </label>
  );
}

function Suffix({ unit, children }: { unit: string; children: ReactNode }) {
  return (
    <div className="fn-input-group">
      {children}
      <span className="fn-input-group__suffix">{unit}</span>
    </div>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
